import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, CircleMarker, Polyline, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Truck, MapPin, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Track() {
  const { token } = useParams();
  const [load, setLoad] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const fetchIt = () => axios.get(`${BACKEND}/api/track/${token}`).then((r) => setLoad(r.data)).catch((e) => setErr(e?.response?.data?.detail || 'Link not found'));
    fetchIt();
    const t = setInterval(fetchIt, 30000); // live-ish refresh
    return () => clearInterval(t);
  }, [token]);

  if (err) return <Centered><AlertTriangle className="w-10 h-10 text-orange-500 mb-3" /><h1 className="font-display font-bold text-2xl mb-1">Tracking unavailable</h1><p className="text-zinc-500">{err}</p></Centered>;
  if (!load) return <Centered><div className="animate-pulse text-zinc-400">Loading tracking…</div></Centered>;

  const wps = load.waypoints || [];
  const progress = new Set(load.progress || []);
  const path = load.geometry && load.geometry.length > 1 ? load.geometry
    : (load.origin_lat != null && load.dest_lat != null ? [[load.origin_lat, load.origin_lng], [load.dest_lat, load.dest_lng]] : null);
  const pins = wps.map((w, i) => ({ w, i })).filter((x) => x.w.lat != null);
  const center = load.origin_lat != null ? [load.origin_lat, load.origin_lng] : (pins[0] ? [pins[0].w.lat, pins[0].w.lng] : [-41.2, 172.5]);
  const done = wps.length ? Math.round((progress.size / wps.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-zinc-900 text-white px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-2">
          <Truck className="w-5 h-5 text-orange-400" />
          <span className="font-display font-bold">Live load tracking</span>
          <span className="ml-auto text-xs text-zinc-400">PTT NZ Alerts</span>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="card mb-4" data-testid="track-summary">
          <h1 className="font-display font-black text-2xl">{load.title}</h1>
          <p className="text-zinc-600">{load.origin} → {load.destination} · {load.date} · {load.direction}</p>
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            {load.pilot_company && <span className="chip chip-outline-zinc">{load.pilot_company}</span>}
            {['width_m', 'height_m', 'length_m'].map((k) => load[k] && <span key={k} className="chip chip-outline-teal">{k[0].toUpperCase()} {load[k]}m</span>)}
            {load.weight_t && <span className="chip chip-outline-teal">{load.weight_t}t</span>}
            <span className={`chip ${load.status === 'completed' ? 'chip-outline-zinc' : 'chip-outline-orange'}`}>{load.status}</span>
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-xs text-zinc-500 mb-1"><span>Progress</span><span>{progress.size}/{wps.length} waypoints</span></div>
            <div className="h-2.5 rounded-full bg-zinc-100 overflow-hidden"><div className="h-full bg-teal-500 transition-all" style={{ width: `${done}%` }} data-testid="track-progress-bar" /></div>
          </div>
        </div>

        <div className="card p-0 overflow-hidden mb-4" style={{ height: 360 }}>
          <MapContainer center={center} zoom={7} style={{ height: '100%', width: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {path && <Polyline positions={path} pathOptions={{ color: '#0d9488', weight: 4, opacity: 0.6, dashArray: '6 8' }} />}
            {load.origin_lat != null && <CircleMarker center={[load.origin_lat, load.origin_lng]} radius={7} pathOptions={{ color: '#0f766e', fillColor: '#2DD4B0', fillOpacity: 1 }}><Popup>Origin</Popup></CircleMarker>}
            {load.dest_lat != null && <CircleMarker center={[load.dest_lat, load.dest_lng]} radius={7} pathOptions={{ color: '#b45309', fillColor: '#F7941D', fillOpacity: 1 }}><Popup>Destination</Popup></CircleMarker>}
            {pins.map(({ w, i }) => (
              <CircleMarker key={i} center={[w.lat, w.lng]} radius={6} pathOptions={{ color: '#fff', weight: 2, fillColor: progress.has(i) ? '#10B981' : '#6366F1', fillOpacity: 1 }}>
                <Popup>{i + 1}. {w.name}{w.eta ? ` · ${w.eta}` : ''}{progress.has(i) ? ' ✓ reached' : ''}</Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>

        <div className="card" data-testid="track-waypoints">
          <h2 className="font-display font-bold text-lg mb-3">Waypoints</h2>
          {wps.length === 0 && <p className="text-sm text-zinc-500">No waypoints on this load.</p>}
          <ol className="space-y-2">
            {wps.map((w, i) => (
              <li key={i} className="flex items-center gap-3 text-sm">
                {progress.has(i) ? <CheckCircle2 className="w-5 h-5 text-teal-500 shrink-0" /> : <MapPin className="w-5 h-5 text-zinc-300 shrink-0" />}
                <span className={`flex-1 ${progress.has(i) ? 'text-zinc-400 line-through' : 'text-zinc-800 font-medium'}`}>{i + 1}. {w.name}</span>
                {w.eta && <span className="text-xs text-zinc-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{w.eta}</span>}
              </li>
            ))}
          </ol>
        </div>
        <p className="text-center text-xs text-zinc-400 mt-6">Auto-refreshes every 30s · Data © Waka Kotahi NZTA</p>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex flex-col items-center justify-center text-center px-4 bg-zinc-50">{children}</div>;
}
